import {PageTitle} from "../../../Components/Landmarks";
import type { ContextProps } from '../../../types';
import React, {useEffect, useRef, useState} from "react";
import {locations} from "../../../Utilities/Location";
import {GridCell} from "../../../Components/Grid";
import {emptyResourceModel} from "../../Library/libraryFactory";
import {createListFromArrayOfObjects} from "../../../Utilities/Types/listUtilities";
import type { ListItem } from "../../../Utilities/Types/listUtilities";
import {orderEditViewModel, orderRequestModel} from "./orderRequestFactory";
import {isGuidEmpty, isNullOrUndefined} from "../../../Utilities/commonUtilities";
import {OrderRequestView} from "./OrderRequestView";
import {orderRequestApi} from "./orderRequestApi";
import {shippingLocationApi} from "../ShippingLocation/shippingLocationApi";
import {ORDER_STATUS, ORDER_TYPE} from "../orderConstants";
import {STUDENT_APPROVAL_STATUS, STUDENT_STATUS} from "../../Student/studentConstants";

export const OrderRequestContainer = ({
                                          catalogFormatId,
                                          catalogItemId,
                                          handleApiCall,
                                          handleReturnRedirect,
                                          orderId,
                                          redirect,
                                          userId,
                                      }: ContextProps) => {

    const [orderRequestDetails, setOrderRequestDetails] = useState<Record<string, unknown> | null>(null);
    const [students, setStudents] = useState<ListItem[]>();
    const [publishers, setPublishers] = useState<ListItem[]>();
    const [shippingLocations, setShippingLocations] = useState<ListItem[]>();
    const shippingLocationsDetails = useRef<Record<string, unknown>[] | null>(null);
    const title = orderRequestDetails?.orderType === ORDER_TYPE.Order ? `Order` : `Special Order Request`;
    const isViewOnly = !(isNullOrUndefined(orderId) || isGuidEmpty(orderId));

    const handleAddLocation = () => redirect(locations.SHIPPING_LOCATION_ADD.path);

    const handleRemoveLocation = async (shippingLocationId: string) => {
        if (confirm(`This will remove the location from your Active Locations. Are you sure you want to continue?`))
            handleApiCall(() => shippingLocationApi.removeShippingLocation(shippingLocationId), setShippingLocationResult);
    };

    const handleSubmitOrderRequest = async (orderRequest: Record<string, unknown>) => {

        const sanitizedModel = orderRequestModel(orderRequest);

        handleApiCall(() => orderRequestApi.addOrder(sanitizedModel), () => {
            handleReturnRedirect();
        });
    };

    const handleCancelOrder = async () => {

        if (orderRequestDetails) {
            (orderRequestDetails as Record<string, unknown>).queueStatus = ORDER_STATUS.OrderReceivedFromPatron;
        }

        handleApiCall(() => orderRequestApi.cancelOrder(orderId), () => {
            handleReturnRedirect();
        });
    }

    const loadOrderRequestDetailsResult = (result: Record<string, unknown>) => {

        const {
            copies,
            format,
            publisherId,
            numberOfCopiesAvailable
        } = (result.formats as Record<string, unknown>[]).find((f: Record<string, unknown>) => f.catalogFormatId === catalogFormatId) as Record<string, unknown>;

        const orderRequestResult = orderRequestModel({
            ...result,
            shippingLocationId: ``,
            authors: result.author,
            catalogFormatId: catalogFormatId,
            format: format,
            gradeLevel: result.gradeLevel,
            isbn: result.isbn13 ? result.isbn13 : (result.isbn10 ? result.isbn10 : result.previewIsbn),
            itemName: result.title,
            orderType: (copies as unknown[]).length === 0 ? ORDER_TYPE.SpecialOrderRequest : (copies as unknown[]).length !== 0 && numberOfCopiesAvailable === 0 ? ORDER_TYPE.Order : ORDER_TYPE.SpecialOrderRequest,
            publisherId: publisherId,
        });

        setOrderRequestDetails(orderRequestResult);
    };

    const loadOrderResult = (result: Record<string, unknown>) => {
        const order = orderEditViewModel(result);
        setOrderRequestDetails(order);
    };

    const setShippingLocationResult = (results: Record<string, unknown>[]) => {
        shippingLocationsDetails.current = results;
        setShippingLocations(createListFromArrayOfObjects(results, `shippingLocationId`, `name`));
    }

    useEffect(() => {

        const getResource = () => {
            handleApiCall(() => orderRequestApi.getResource(catalogItemId), (results = emptyResourceModel) => loadOrderRequestDetailsResult(results));
        }

        const getOrderDetails = () => {
            handleApiCall(() => orderRequestApi.getOrder(orderId), (result: Record<string, unknown>) => loadOrderResult(result));
        }

        const getStudents = () => {
            handleApiCall(() => orderRequestApi.getStudents({
                userId: userId,
                isActive: true,
                studentApprovalStatus: STUDENT_APPROVAL_STATUS.Approved.id,
                studentStatus: STUDENT_STATUS.Active.id,
                forDropDown: true,
            }), (results: Record<string, unknown>[]) => {
                setStudents(createListFromArrayOfObjects(results, `studentId`, `fullName`));
            });
        };

        const getShippingLocations = () => {
            handleApiCall(() => orderRequestApi.getShippingLocations(), setShippingLocationResult);
        }

        const getPublishers = () => {
            handleApiCall(() => orderRequestApi.getPublishers(), (results: Record<string, unknown>[]) => {
                setPublishers(createListFromArrayOfObjects(results, `publisherId`, `name`));
            });
        }

        if (orderId && !isGuidEmpty(orderId))
            getOrderDetails();
        else if (catalogItemId && !isGuidEmpty(catalogItemId))
            getResource();
        else if (isGuidEmpty(catalogItemId))
            setOrderRequestDetails(orderRequestModel());

        if (!shippingLocations)
            getShippingLocations();
        if (!publishers)
            getPublishers();
        if (!students)
            getStudents();

    }, [catalogItemId, orderId]);

    return <>
        <PageTitle h1={title}/>{
        !isNullOrUndefined(orderRequestDetails) &&
        <>
            <GridCell>
                {
                    orderRequestDetails.orderType === ORDER_TYPE.SpecialOrderRequest &&
                    <p>If you have searched for a title and you have not found the item you are looking for, you can
                        fill out this special order request form and the FIMC-VI staff will then research the book and
                        inform you of the results. <span className={`alert-color`}>Special Orders take extra time to fulfill.</span>
                    </p>
                }
                {
                    orderRequestDetails.orderType !== ORDER_TYPE.SpecialOrderRequest &&
                    <p>
                        We are currently out of copies for this item, you can fill out this form
                        to request copies and the FIMC-VI staff will inform you of the results. <span
                        className={`alert-color`}>Orders take time to fulfill.</span>
                    </p>
                }
                {
                    isViewOnly &&
                    <p><b>If the &lsquo;Cancel order&rsquo; button is not showing for an order, please contact FIMC-VI
                        to
                        cancel or make changes to an order</b></p>
                }

            </GridCell>
            {
                shippingLocations && students &&
                <OrderRequestView
                    shippingLocations={shippingLocations}
                    shippingLocationsDetails={shippingLocationsDetails.current}
                    handleAddLocation={handleAddLocation}
                    handleCancelOrder={handleCancelOrder}
                    handleRemoveLocation={handleRemoveLocation}
                    handleReturn={handleReturnRedirect}
                    handleSubmitOrderRequest={handleSubmitOrderRequest}
                    publishers={publishers}
                    orderRequestDetails={orderRequestDetails}
                    students={students}
                    userId={userId}
                    viewOnly={isViewOnly}
                />
            }
        </>
    }
    </>;
};

